import { BrightcoveVideo } from "@components/BrightcoveVideo/BrightcoveVideo";
import ArticleImage from "@components/Carousel/ArticleImage";
import RelatedStoriesCarousel from "@components/Carousel/RelatedStoriesCarousel";
import { AdsImu1, AdsMidArticle } from "@elements/Advertisement/variant/AdsImu";
import {
  CompanionAdsImu1,
  CompanionAdsMidArticle,
} from "@elements/Advertisement/variant/CompanionAdImu";
import ApplyParagraphAnnotations from "@elements/ArticleParagraph/ApplyParagraphAnnotations";
import { YOUTUBE_WATCH_URL } from "@elements/ArticleParagraph/Embeds/constants";
import Embed from "@elements/ArticleParagraph/Embeds/Embed";
import YouTubeEmbedCustom from "@elements/ArticleParagraph/Embeds/YouTubeEmbedCustom";
import ParagraphList from "@elements/ArticleParagraph/ParagraphElements/ParagraphList";
import PullQuoteCard from "@elements/ArticleParagraph/ParagraphElements/PullQuoteCard";
import SubHead from "@elements/ArticleParagraph/ParagraphElements/SubHead";
import WrapperContainer from "@elements/ArticleParagraph/ParagraphElements/WrapperContainer";
import Paragraph from "@elements/Typography/Paragraph";
import { RenderTags } from "@elements/Typography/Tags";
import { colorConstants } from "@helper/sectionConstants";
import { isArrayWithElements } from "@helper/utils";
import { ProcessedArticleData } from "@transformer/useOsResponse";
import {
  ArticleAdPageTargetingType,
  ArticleAdSlotTargetingType,
  PageAdTargetingTypeEnum,
} from "@typings/Ads.d";
import {
  type ProcessedArticleBody,
  ProcessedAuthorContent,
  ProcessedTagsContent,
} from "@typings/Opensearch";
import { HandleReactionClick, ReactionProp } from "@typings/Reactions";
import React, { useEffect, useState } from "react";

import { ArticleReactionOptions } from "../Reactions/ArticleReactionOptions";

export type ArticleParagraphProps = {
  articleId: string;
  articleTitle: string;
  articleBody: ProcessedArticleBody;
  relatedStories?: ProcessedArticleData[];
  sectionName: string;
  subSectionName?: string;
  tags?: ProcessedTagsContent[];
  pageUrl: string;
  pageNumber: number;
  dateCreated: string;
  isInfiniteScrollArticle?: boolean;
  authors?: ProcessedAuthorContent[];
  articlePageTarget?: ArticleAdPageTargetingType[];
  articleSlotTarget?: ArticleAdSlotTargetingType[];
  gsChannels?: string[];
  enableReadMore?: boolean;
  showReactions: boolean;
  reactions: ReactionProp[];
  handleReactionClick: HandleReactionClick;
  userReactions: string;
};

const READMORE_PARA_COUNT = 3; // Count for read more paras.

export default function ArticleParagraph({
  articleId,
  articleBody,
  sectionName,
  subSectionName,
  relatedStories,
  tags,
  articlePageTarget,
  articleSlotTarget,
  isInfiniteScrollArticle,
  pageNumber,
  gsChannels,
  enableReadMore = false,
  showReactions,
  reactions,
  handleReactionClick,
  userReactions,
}: ArticleParagraphProps) {
  const [articleParagraphs, setArticleParagraphs] =
    useState<ProcessedArticleBody | null>(articleBody);

  // Get count of paragraphs in article body.
  const totalParaCount = articleBody.reduce(
    (e, element) => (element.type === "paragraph" ? e + 1 : e),
    0,
  );

  const [showFullArticle, setShowFullArticle] = useState(
    totalParaCount < READMORE_PARA_COUNT ? true : !enableReadMore,
  );

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setArticleParagraphs(articleBody);
  }, [articleBody]);

  const sectionColor =
    sectionName && sectionName in colorConstants
      ? colorConstants[sectionName as keyof typeof colorConstants].color.lightBg
      : "";

  const adSectionName = subSectionName
    ? `${sectionName}/${subSectionName.replace(/[^A-Za-z0-9]+/g, "-")}`
    : sectionName;

  let paragraphCount = 0;
  const pageAdTarget = PageAdTargetingTypeEnum.ARTICLE;

  let adsImu1 = (
    <div className="mb-5 justify-items-center">
      <AdsImu1
        pageAdTargetType={pageAdTarget}
        sectionName={adSectionName}
        rootClassName={"px-xxm py-5 mx-auto w-fit"}
        articlePageTarget={articlePageTarget}
        articleSlotTarget={articleSlotTarget}
        gsChannels={gsChannels}
      />
    </div>
  );
  let adsMidArticle = (
    <div className="mb-5 justify-items-center">
      <AdsMidArticle
        pageAdTargetType={pageAdTarget}
        sectionName={adSectionName}
        rootClassName={"px-xxm py-5 mx-auto w-fit"}
        articlePageTarget={articlePageTarget}
        articleSlotTarget={articleSlotTarget}
        gsChannels={gsChannels}
      />
    </div>
  );

  if (isInfiniteScrollArticle) {
    adsImu1 = (
      <div className="mb-5 justify-items-center">
        <CompanionAdsImu1
          uniqueSectionName={adSectionName}
          pageAdTargetType={pageAdTarget}
          pageNumber={pageNumber}
          articlePageTarget={articlePageTarget}
          articleSlotTarget={articleSlotTarget}
          rootClassName={"px-xxm py-5 mx-auto w-fit"}
          gsChannels={gsChannels}
        />
      </div>
    );
    adsMidArticle = (
      <div className="mb-5 justify-items-center">
        <CompanionAdsMidArticle
          uniqueSectionName={adSectionName}
          pageAdTargetType={pageAdTarget}
          pageNumber={pageNumber}
          articlePageTarget={articlePageTarget}
          articleSlotTarget={articleSlotTarget}
          rootClassName={"px-xxm py-5 mx-auto w-fit"}
          gsChannels={gsChannels}
        />
      </div>
    );
  }

  return (
    <div
      data-testid="article-body"
      className={`article-body-wrapper-${articleId} relative inline-block w-full`}
    >
      <div className={!showFullArticle ? "relative" : ""}>
        {articleParagraphs &&
          articleParagraphs.map((element, index) => {
            let bannersAndAds = null;
            if (showFullArticle && paragraphCount === 2) {
              bannersAndAds = <>{adsImu1}</>;
            } else if (showFullArticle && paragraphCount === 5) {
              bannersAndAds = <>{adsMidArticle}</>;
            }

            switch (element.type) {
              case "paragraph":
                paragraphCount++;

                if (!showFullArticle && paragraphCount > READMORE_PARA_COUNT) {
                  return null;
                }

                return (
                  <React.Fragment key={index}>
                    <Paragraph
                      testid={`paragraph-${index}`}
                      className={paragraphCount == 1 ? "article-summary" : ""}
                    >
                      {ApplyParagraphAnnotations(
                        element.value,
                        element.annotations,
                      )}
                    </Paragraph>
                    {bannersAndAds}
                  </React.Fragment>
                );

              case "sub_head":
                return showFullArticle ? (
                  <React.Fragment key={index}>
                    <SubHead element={element} />
                  </React.Fragment>
                ) : null;

              case "image": {
                if (!showFullArticle) return null;
                const imageHeight =
                  element.image?.height && element.image.height >= 1460
                    ? 1460
                    : 550;
                return (
                  <ArticleImage
                    key={index}
                    url={element.image?.url}
                    alt={element.caption}
                    height={imageHeight}
                    caption={element.caption}
                    credit={element.credit}
                    className="mb-10 mt-2"
                    sectionName={sectionName}
                  />
                );
              }

              case "embed":
                return showFullArticle ? (
                  <React.Fragment key={index}>
                    <Embed
                      className="my-lg px-3 md:px-5 lg:px-0"
                      src={element.embedUrl}
                    />
                  </React.Fragment>
                ) : null;

              case "pull_quote":
                return showFullArticle ? (
                  <React.Fragment key={index}>
                    <PullQuoteCard
                      quote={element.quote}
                      quotee={element.quotee}
                      quoteCredit={element.quoteCredit}
                      sectionColor={sectionColor}
                    />
                  </React.Fragment>
                ) : null;

              case "list_bulleted":
              case "list_numbered": {
                if (!showFullArticle) return null;
                const listType = element.type === "list_bulleted" ? "ol" : "ul";
                const listItems = element.listItem;
                const listStyle =
                  element.listStyle === "decimal"
                    ? "list-decimal"
                    : "list-disc";
                return (
                  <React.Fragment key={index}>
                    <ParagraphList
                      listStyle={listStyle}
                      listType={listType}
                      listItems={listItems}
                    />
                  </React.Fragment>
                );
              }

              case "youtube_video": {
                if (!showFullArticle) return null;
                const url = `${YOUTUBE_WATCH_URL}${element.youtubeId}`;
                return (
                  <React.Fragment key={index}>
                    <YouTubeEmbedCustom
                      url={url}
                      className="my-lg px-3 md:px-5 lg:px-0"
                    />
                  </React.Fragment>
                );
              }

              case "brightcove_video": {
                return showFullArticle ? (
                  <React.Fragment key={index}>
                    <WrapperContainer className="block my-lg px-3 md:px-5 lg:px-0">
                      <BrightcoveVideo videoId={element.videoId} />
                    </WrapperContainer>
                  </React.Fragment>
                ) : null;
              }

              default:
                return null;
            }
          })}
        {!showFullArticle && (
          <div className="pointer-events-none absolute bottom-0 left-0 h-32 w-full bg-gradient-to-t from-white via-white/80 to-transparent" />
        )}
      </div>

      {/* Read More Button */}
      {!showFullArticle && (
        <div className="flex justify-center mb-6">
          <button
            onClick={() => {
              setShowFullArticle(true);
            }}
            className="px-10 py-xxxsm h-10 text-sm font-bold leading-5 text-grey-200 font-opensans border border-solid border-grey-800"
            data-testid="read-more-button"
          >
            READ MORE
          </button>
        </div>
      )}

      {showReactions && reactions.length > 0 && (
        <div className="my-5 lg:my-8">
          <ArticleReactionOptions
            reactions={reactions}
            handleReactionClick={handleReactionClick}
            reactionsUser={userReactions}
            articleId={articleId}
            variant="bottom"
          />
        </div>
      )}

      {showFullArticle &&
        relatedStories &&
        isArrayWithElements(relatedStories) && (
          <RelatedStoriesCarousel
            relatedArticles={relatedStories}
            rootClassName="overflow-hidden bg-red-700 px-3 py-5 my-10 px-3 md:px-5 lg:pr-0 lg:pl-3"
            testId="related-article-carousel"
            source={"related-bottom"}
          />
        )}

      {/* Tags dont dont render If alias not available in payload*/}
      {showFullArticle && isArrayWithElements(tags) && (
        <RenderTags
          tagsWrapper="mb-5 lg:mb-6 px-3 md:px-5 lg:px-0"
          tags={tags}
          refMedium={"article-bottom"}
        />
      )}
    </div>
  );
}
